<template>
  <v-card flat dense :loading="loading">
    <v-card-title> <slot name="header" /> </v-card-title>
    <v-card-text>
      <v-form ref="form" v-model="isValid">
        <v-card class="mx-auto ma-0" tile flat>
          <v-card-text class="pt-0">
            <v-row>
              <v-col>
                <v-row v-if="showEmployee">
                  <v-autocomplete
                    v-model="localAmount.employee_id"
                    :label="`${$t('EMPLOYEES.ITEM')} *`"
                    :search-input.sync="employee"
                    :items="employees"
                    :loading="loading"
                    :rules="getRulesByName('employee')"
                    item-text="name"
                    item-value="id"
                    clearable
                    :no-data-text="$t('TASKS.NO_DATA_FILTER_TEXT')"
                  ></v-autocomplete>
                </v-row>
                <v-row>
                  <v-menu
                    ref="menuDate"
                    v-model="menuDate"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="localAmount.date | dateFormat"
                        :label="`${$t('ITEM.DATE')} *`"
                        append-icon="mdi-calendar"
                        :disabled="!showEmployee"
                        clearable
                        readonly
                        :rules="getRulesByName('date')"
                        role="button"
                        autocomplete="nope"
                        @click:clear="clear_date"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      ref="picker"
                      v-model="localAmount.date"
                      :max="maxDate"
                      first-day-of-week="1"
                      @change="saveDate"
                    ></v-date-picker>
                  </v-menu>
                </v-row>
                <v-row>
                  <v-text-field
                    ref="kilometers"
                    v-model.number="localAmount.kilometers"
                    name="kilometers"
                    suffix="km"
                    type="number"
                    :rules="getRulesByName('kilometers')"
                    counter="5"
                    min="0"
                    step="1"
                    :label="$t('PLANNINGS.DRIVEN_KILOMETERS')"
                    :error-messages="errors.kilometers"
                    @input="
                      updateValue('kilometers', $event),
                        updateValue('expenses', $event)
                    "
                  ></v-text-field>
                </v-row>
                <v-row>
                  <money-input
                    v-model="localAmount.expenses"
                    name="expenses"
                    :rules="getRulesByName('expenses')"
                    :label="$t('PLANNINGS.EXPENSES')"
                    :error-messages="errors.expenses"
                    @input="
                      updateValue('expenses', $event),
                        updateValue('kilometers', $event)
                    "
                  />
                </v-row>
                <v-row>
                  <v-textarea
                    v-model="localAmount.notes"
                    name="notes"
                    class="pt-3 mt-2"
                    :rows="6"
                    :label="`${$t('PLANNINGS.NOTES')} *`"
                    :rules="getRulesByName('notes')"
                    :error-messages="errors.notes"
                    outlined
                    counter="1500"
                    @input="updateValue('notes', $event)"
                  ></v-textarea>
                </v-row>
                <v-row>
                  <auto-upload-file-input
                    v-model="localAmount.uploads"
                    :label="$t('ITEM.FILES')"
                    :url="`uploads/expense-reports`"
                    :error-messages="errors.upload_ids"
                    prepend-icon="mdi-file"
                    chips
                    multiple
                    show-size
                    @input="localAmount.upload_ids = $event"
                  ></auto-upload-file-input>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="px-0">
            <slot name="buttons" />
          </v-card-actions>
        </v-card>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex';
import AutoUploadFileInput from '@/modules/_global/_components/AutouploadFileInput';
import { debounce, isEmpty } from 'lodash';
import formEditable from '@/modules/_global/mixins/form-editable';
import {
  requiredTextDescription,
  notEmpty,
  positiveNumber,
  maxLength,
  maxLengthPriceNumber,
  positivePriceNumber
} from '@/modules/_global/variables/rules';
import { dateFormat } from '@/modules/_global/filters';
import moment from 'moment';
import MoneyInput from '@/modules/_global/_components/money/MoneyInput.vue';

export default {
  name: 'AmountReportForm',

  components: {
    MoneyInput,
    AutoUploadFileInput
  },

  filters: {
    dateFormat
  },

  mixins: [formEditable],

  props: {
    amount: {
      type: Object,
      default: () => null
    }
  },

  data: () => ({
    isValid: true,
    employees: [],
    employee: null,
    localAmount: {
      uploads: [],
      upload_ids: []
    },
    menuDate: false,
    showEmployee: true,
    maxDate: '1940-01-01',
    rules: {
      employee: [notEmpty],
      date: [notEmpty],
      kilometers: [maxLength(5), positiveNumber],
      expenses: [maxLengthPriceNumber(9), positivePriceNumber],
      notes: requiredTextDescription
    }
  }),

  computed: {
    loading() {
      return this.$store.getters['loading'];
    },
    debouncedRequest() {
      return debounce(this.expensiveFunc, this.delay);
    }
  },

  watch: {
    isValid() {
      this.$emit('isValid', this.isValid);
    },
    localAmount: {
      deep: true,
      handler() {
        this.$emit('setAmount', this.localAmount);
      }
    },
    employee(val) {
      if (val !== this.localAmount.employee && !isEmpty(val)) {
        this.expensiveFunc = this.getDataEmployees;
        this.debouncedRequest();
      }
    }
  },

  mounted() {
    this.maxDate = moment().format('YYYY-MM-DD');
    if (this.amount !== null) {
      this.getAmount();
      this.showEmployee = false;
    }
  },

  methods: {
    ...mapActions('ereporter', [
      'getAmountReport',
      'getFilteredPayrollEmployee'
    ]),
    getDataEmployees() {
      this.getFilteredPayrollEmployee({ search: this.employee })
        .then(({ data }) => {
          this.employees = data.data;
        })
        .catch(({ response }) => {
          console.log(response);
        });
    },
    getAmount() {
      this.getAmountReport({ amountId: this.amount.id })
        .then(({ data }) => {
          this.localAmount = data;
        })
        .catch(({ response }) => {
          this.handleErrorsByResponse(response);
        });
    },

    saveDate(date) {
      this.$refs.menuDate.save(date);
    },
    clear_date() {
      this.localAmount.date = null;
    },

    setValid(index, value) {
      this.$set(this.valid, index, value);
    }
  }
};
</script>
